import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0User } from '@hooks/useAuth0User';
import { useDeviceOs } from '@hooks/useDeviceOs';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@lib/constants';
import { isUserAuthenticated } from '@lib/utils';
import {
  getStoredAppUpsell,
  setStoredAppUpsell
} from '@lib/app-upsell-storage';

const BANNER_DISMISSAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export type AppUpsellCtaLabel = 'Get the app' | 'Update';

/**
 * Decides whether a My Team visit gets the mobile-app upsell banner.
 *
 * Eligible audience: logged-in members on a mobile device or inside the
 * app's webview, with the PostHog flag on. Logged-out users never see it
 * (My Team redirects them away regardless). Every eligible My Team
 * navigation shows the banner; dismissing it (or tapping its CTA) hides it
 * for 7 days, after which it returns.
 *
 * The hook decides once per mount. Per-navigation behaviour comes from
 * _app rendering the My Team subtree inside an ErrorBoundary keyed by
 * router.pathname, which remounts this hook on every page change.
 */
export const useAppUpsell = (): {
  isBannerVisible: boolean;
  storeUrl: string;
  ctaLabel: AppUpsellCtaLabel;
  isAppView: boolean;
  dismiss: () => void;
} => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { isIos, isMobile } = useDeviceOs();
  const { isAppView } = useIsAppView();
  const isUpsellEnabled = usePostHogFeatureFlag(
    PostHogFeatureFlags.appUpsellIsLive,
    false
  );

  const isEligible =
    isUpsellEnabled && isUserAuthenticated(user) && (isMobile || isAppView);

  const [isBannerVisible, setIsBannerVisible] = useState(false);
  // Latches once the banner has been decided so re-renders (or the flag/user
  // settling after the decision) can't re-show a dismissed banner, while
  // still letting late-arriving flag/user data make the first positive
  // decision.
  const hasDecidedRef = useRef(false);

  useEffect(() => {
    if (hasDecidedRef.current || isUserLoading || !isEligible) return;

    hasDecidedRef.current = true;

    const stored = getStoredAppUpsell();
    // A future timestamp (clock skew) yields a negative elapsed time and
    // keeps the banner hidden rather than re-showing it early.
    const wasDismissedRecently =
      stored !== null &&
      Date.now() - stored.bannerDismissedAt < BANNER_DISMISSAL_DURATION_MS;
    if (!wasDismissedRecently) {
      setIsBannerVisible(true);
    }
  }, [isEligible, isUserLoading]);

  const dismiss = useCallback(() => {
    setStoredAppUpsell({ bannerDismissedAt: Date.now() });
    setIsBannerVisible(false);
  }, []);

  return {
    // Re-checked on every render so a session expiring, or the flag being
    // switched off, hides the banner before My Team redirects or remounts.
    isBannerVisible: isBannerVisible && isEligible,
    storeUrl: isIos ? APP_STORE_URL : GOOGLE_PLAY_URL,
    ctaLabel: isAppView ? 'Update' : 'Get the app',
    isAppView,
    dismiss
  };
};
